import React, { useEffect, useRef, useState } from "react";
import { Button } from "@mantine/core";
import flat from "flat";
import { BeatLoader } from "react-spinners";
import { loadingStates, validateEmail } from "../../utilities/utilities";
import { showNotification } from "@mantine/notifications";
import { apiWrapWithErrorWithData } from "../../utilities/apiHelpers";
import { getPartiesList } from "../../utilities/apis/parties";
import { getAgreementDetails } from "../../utilities/apis/agreements";
import { getOrgDetailsFromMaster } from "../../utilities/apis/users";
import { getParentFiles } from "../../utilities/apis/commonApis";
import useMultiFileUpload from "../../hooks/useMultiFileUpload";
import LegacyAgreementParty from "./LegacyAgreementParty";
import { KYC_FILE_FIELDS, hasUpload } from "./AgreementParty";

const PARTY_KEYS = [
  "firstParty",
  "secondParty",
  "thirdParty",
  "fourthParty",
  "fifthParty",
  "sixthParty",
  "seventhParty",
  "eighthParty",
  "ninthParty",
  "tenthParty",
  "eleventhParty",
  "twelthParty",
  "thirteenthParty",
  "fourteenthParty",
  "fifteenthParty",
  "sixteenthParty",
  "seventeenthParty",
  "eighteenthParty",
  "nineteenthParty",
  "twentythParty",
];

const LegacyAgreementForm3 = ({
  setLegacyAgreementFormValue,
  legacyAgreementFormValue,
  uiConfigs,
  setUiConfigs,
  saveAgreement,
  agreementId,
}) => {
  const [dropDownNames, setDropDownNames] = useState([]);
  const [parties, setParties] = useState([]);
  const [errors, setErrors] = useState({});
  const [addParty, setAddParty] = useState(2);
  const [loader, setLoader] = useState(false);
  const [dataRole, setDataRole] = useState({});
  const kycHydratedRef = useRef(false);
  const { addFiles } = useMultiFileUpload({
    loadFromParent: false,
    parent: "agreement",
    parentId: agreementId,
  });

  
  const clearPartyErrors = (partyName, fields) => {
    const partyData = legacyAgreementFormValue[partyName] || {};

    const hasValue = (field) => {
      const v = partyData[field];
      if (v instanceof File) return true;
      if (v && typeof v === "object" && hasUpload(v)) return true;
      return v !== undefined && v !== null && String(v).trim() !== "";
    };

    setErrors((prev) => {
      if (!prev[partyName]) return prev;

      const targetFields = fields ?? Object.keys(prev[partyName]);
      const partyErrors = { ...prev[partyName] };

      targetFields.forEach((f) => {
        if (hasValue(f)) delete partyErrors[f];
      });

      return { ...prev, [partyName]: partyErrors };
    });
  };

  const partiesApi = async (name) => {
    const { data: res } = await apiWrapWithErrorWithData(
      getPartiesList({ name }),
    );
    setParties(res);
    setDropDownNames(res.map(({ name: val }) => val));
  };

  const fetchOrgDetails = async () => {
    try {
      const resp = await apiWrapWithErrorWithData(getOrgDetailsFromMaster());
      if (resp.success && resp.result?.length > 0) {
        const orgData = resp.result[0];
        setLegacyAgreementFormValue((prev) => {
          if (prev.firstParty?.name) return prev;
          return {
            ...prev,
            firstParty: {
              ...prev.firstParty,
              name: orgData.name || "",
              entityType: orgData.details?.entityType || "",
              address: orgData.details?.regAddress || "",
              brandName: orgData.details?.brandName || "",
              corporateAddress: orgData.details?.corporateAddress || "",
              email: orgData.details?.signEmail || "",
              representative: orgData.details?.poc || "",
              type: "Entity",
            },
          };
        });
      }
    } catch (error) {
      console.error("Error fetching organization details:", error);
    }
  };

  useEffect(() => {
    for (let i = 20; i >= 1; i--) {
      const partyName = PARTY_KEYS[i - 1];
      const partyObj = legacyAgreementFormValue[partyName];
      if (
        partyObj &&
        Object.keys(partyObj).length > 0 &&
        (partyObj.name || partyObj.indi_name)
      ) {
        setAddParty(Math.max(2, i));
        break;
      }
    }
    partiesApi();
    fetchOrgDetails();
  }, []);

  useEffect(() => {
    if (!agreementId) return;
    const fetchFlowData = async () => {
      const data = await apiWrapWithErrorWithData(
        getAgreementDetails({ agreementId }),
      );
      if (data.agreement?.flowJson) {
        setDataRole(data.agreement.flowJson);
      }

      const filesResp = await apiWrapWithErrorWithData(
        getParentFiles({ parent: "agreement", parentId: agreementId }),
      );
      if (!filesResp?.files?.length) return;

      setLegacyAgreementFormValue((prev) => {
        const next = { ...prev };
        filesResp.files.forEach((f) => {
          if (!f.field?.includes(".")) return;
          const [party, field] = f.field.split(".");
          if (!FILE_FIELDS.includes(field)) return;
          next[party] = {
            ...(next[party] || {}),
            [field]: {
              name: f.fileName,
              fileId: f.id,
              hostedLink: f.hostedLink,
              field: f.field,
            },
          };
        });
        return next;
      });
    };
    fetchFlowData();
  }, [agreementId]);

  useEffect(() => {
    if (!agreementId || kycHydratedRef.current) return;

    const hasPartyFiles = PARTY_KEYS.some((partyKey) => {
      const party = legacyAgreementFormValue[partyKey];
      if (!party) return false;
      return KYC_FILE_FIELDS.some((field) => hasUpload(party[field]));
    });
    if (!hasPartyFiles && !legacyAgreementFormValue.formCompleted) return;

    kycHydratedRef.current = true;
    let cancelled = false;

    (async () => {
      const resp = await apiWrapWithErrorWithData(
        getParentFiles({ parent: "agreement", parentId: agreementId }),
      );
      if (cancelled) return;

      const parentFiles = resp?.success ? resp.files || [] : [];

      setLegacyAgreementFormValue((prev) => {
        const next = { ...prev };
        PARTY_KEYS.forEach((partyKey) => {
          if (partyKey === "firstParty") return;
          const party = { ...(next[partyKey] || {}) };
          let changed = false;
          KYC_FILE_FIELDS.forEach((field) => {
            const current = party[field];
            if (current instanceof File) return;

            const storedName = current?.name || party[`${field}.name`];
            if (!storedName) return;

            const match = parentFiles.find(
              (f) =>
                (f.fileName || f.name || "").toLowerCase() ===
                storedName.toLowerCase(),
            );

            delete party[`${field}.name`];
            delete party[`${field}.fileId`];
            delete party[`${field}.hostedLink`];
            delete party[`${field}.field`];
            delete party[`${field}.url`];

            party[field] = {
              name: storedName,
              field,
              ...(match
                ? {
                    fileId: match.id || match.fileId,
                    hostedLink: match.hostedLink || match.url,
                  }
                : {
                    ...(current?.fileId ? { fileId: current.fileId } : {}),
                    ...(current?.hostedLink
                      ? { hostedLink: current.hostedLink }
                      : {}),
                  }),
            };
            changed = true;
          });
          if (changed) next[partyKey] = party;
        });
        return next;
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [agreementId, legacyAgreementFormValue.formCompleted]);

  useEffect(() => {
    if (!legacyAgreementFormValue.businessUnit) return;
    fetchOrgDetails();
  }, [legacyAgreementFormValue.businessUnit]);

  const activePartyNames = PARTY_KEYS.slice(0, addParty)
    .map((key) => legacyAgreementFormValue[key]?.name || "")
    .join("|");

  useEffect(() => {
    if (!parties.length) return;

    PARTY_KEYS.slice(0, addParty).forEach((partyName) => {
      const partyData = legacyAgreementFormValue[partyName];
      if (!partyData?.name) return;

      const matched = parties.find((p) => p.name === partyData.name);
      if (!matched) return;

      setLegacyAgreementFormValue((prev) => {
        const current = prev[partyName] || {};
        const nextEntityType =
          matched.companyType || matched.entityType || "Company";
        if (
          current.email === (matched.email || "") &&
          current.entityType === nextEntityType &&
          current.signatory === (matched.signatory || "")
        ) {
          return prev;
        }

        return {
          ...prev,
          [partyName]: {
            ...current,
            type: matched.type || "Entity",
            entityType: nextEntityType,
            name: matched.name,
            email: matched.email || "",
            pocemail: matched.email || "",
            signatory: matched.signatory || "",
            representative: matched.representative || "",
            brandName: matched.brandName || "",
            address:
              matched.registeredOfficeAddress || matched.residenceOf || "",
            corporateAddress: matched.corporateOfficeAddress || "",
          },
        };
      });

    });
  }, [parties, addParty, activePartyNames]);

  const validate = () => {
    const keys = {};

    const validateParty = (party) => {
      if (!party?.data) return;
      const { data, name } = party;
      const isEntity = data.type === "Entity";
      const isIndividual = data.type === "Individual";

      const err = (field, message) => {
        keys[name] = { ...keys[name], [field]: message };
      };

      // ── First party: only signing authority is required ──────────────────
      if (name === "firstParty") {
        if (
          !dataRole["7"] ||
          dataRole["7"].length === 0 ||
          !dataRole["7"][0]?.userId
        ) {
          err("signingAuthority", "Please select signing authority.");
        }
        return;
      }

      // ── Entity type ──────────────────────────────────────────────────────
      if (isEntity) {
        if (!data.name?.trim())
          err("name", "Please enter legal name.");
        else if (data.name.trim().length < 3)
          err("name", "Legal name must be at least 3 characters.");

        if (!data.entityType)
          err("entityType", "Please select entity type.");

        if (!data.partyType)
          err("partyType", "Please select party type.");

        if (!data.partyCode?.trim())
          err("partyCode", "Please enter party code.");

        if (!data.brandName?.trim())
          err("brandName", "Please enter brand name.");
        else if (data.brandName.trim().length < 2)
          err("brandName", "Brand name must be at least 2 characters.");

        if (!data.signatory?.trim())
          err("signatory", "Please enter authorised signatory name.");
        else if (data.signatory.trim().length < 2)
          err("signatory", "Signatory name must be at least 2 characters.");

        if (!data.email?.trim())
          err("email", "Please enter authorised signatory email.");
        else if (!validateEmail(data.email))
          err("email", "Please enter a valid email address.");

        if (!data.representative?.trim())
          err("representative", "Please enter business POC name.");
        else if (data.representative.trim().length < 2)
          err("representative", "POC name must be at least 2 characters.");

        if (!data.pocemail?.trim())
          err("pocemail", "Please enter business POC email.");
        else if (!validateEmail(data.pocemail))
          err("pocemail", "Please enter a valid email address.");

        if (!data.pocphone?.trim())
          err("pocphone", "Please enter business POC phone.");
        else if (!/^\d{10}$/.test(data.pocphone))
          err("pocphone", "Phone must be exactly 10 digits.");

        if (!data.address?.trim())
          err("address", "Please enter registered address.");
        else if (data.address.trim().length < 5)
          err("address", "Registered address must be at least 5 characters.");

        if (!data.corporateAddress?.trim())
          err("corporateAddress", "Please enter corporate address.");
        else if (data.corporateAddress.trim().length < 5)
          err("corporateAddress", "Corporate address must be at least 5 characters.");

        if (!data.gstNo?.trim()) err("gstNo", "Please enter GSTIN.");
        else if (!/^[A-Z0-9]{15}$/.test(data.gstNo.toUpperCase()))
          err("gstNo", "GSTIN must be exactly 15 alphanumeric characters.");

        if (!data.panNo?.trim()) err("panNo", "Please enter PAN.");
        else if (!/^[A-Z0-9]{10}$/.test(data.panNo.toUpperCase()))
          err("panNo", "PAN must be exactly 10 alphanumeric characters.");

        if (!hasUpload(data.uploadGST))
          err("uploadGST", "Please upload GST certificate.");

        if (data.panNo?.trim() && !hasUpload(data.uploadPAN))
          err("uploadPAN", "Please upload PAN card.");
      }

      // ── Individual type ──────────────────────────────────────────────────
      if (isIndividual) {
        if (!data.indi_name?.trim())
          err("indi_name", "Please enter individual name.");
        else if (data.indi_name.trim().length < 3)
          err("indi_name", "Name must be at least 3 characters.");

        if (!data.indi_fatherName?.trim())
          err("indi_fatherName", "Please enter father's name.");
        else if (data.indi_fatherName.trim().length < 2)
          err("indi_fatherName", "Father's name must be at least 2 characters.");

        if (!data.indi_address?.trim())
          err("indi_address", "Please enter residence address.");
        else if (data.indi_address.trim().length < 5)
          err("indi_address", "Residence address must be at least 5 characters.");

        if (!data.indi_email?.trim())
          err("indi_email", "Please enter email.");
        else if (!validateEmail(data.indi_email))
          err("indi_email", "Please enter a valid email address.");

        if (!data.indi_pan?.trim()) err("indi_pan", "Please enter PAN.");
        else if (!/^[A-Z0-9]{10}$/.test(data.indi_pan.toUpperCase()))
          err("indi_pan", "PAN must be exactly 10 alphanumeric characters.");

        if (!data.indi_phone?.trim())
          err("indi_phone", "Please enter phone number.");
        else if (!/^\d{10}$/.test(data.indi_phone))
          err("indi_phone", "Phone must be exactly 10 digits.");

        if (!data.aadhaarNo?.trim())
          err("aadhaarNo", "Please enter Aadhaar number.");
        else if (!/^\d{12}$/.test(data.aadhaarNo))
          err("aadhaarNo", "Aadhaar must be exactly 12 digits.");

        if (!data.gstNo?.trim()) err("gstNo", "Please enter GSTIN.");
        else if (!/^[A-Z0-9]{15}$/.test(data.gstNo.toUpperCase()))
          err("gstNo", "GSTIN must be exactly 15 alphanumeric characters.");

        if (!hasUpload(data.uploadPAN))
          err("uploadPAN", "Please upload PAN card.");

        if (!hasUpload(data.uploadAadhaar))
          err("uploadAadhaar", "Please upload Aadhaar card.");

        if (!hasUpload(data.uploadGST))
          err("uploadGST", "Please upload GST certificate.");
      }
    };

    const partiesToValidate = PARTY_KEYS.slice(0, addParty).map(
      (partyName) => ({
        data: legacyAgreementFormValue[partyName] || {},
        name: partyName,
      }),
    );

    partiesToValidate.forEach(validateParty);

    const fileEntries = [];
    PARTY_KEYS.slice(0, addParty).forEach((partyKey) => {
      const partyData = legacyAgreementFormValue[partyKey];
      if (!partyData || typeof partyData !== "object") return;
      KYC_FILE_FIELDS.forEach((field) => {
        const value = partyData[field];
        const fileName =
          value instanceof File
            ? value.name
            : value?.name || (typeof value === "string" ? value : null);
        if (fileName) {
          fileEntries.push({
            partyKey,
            field,
            name: fileName.toLowerCase(),
          });
        }
      });
    });

    const nameCounts = fileEntries.reduce((acc, { name }) => {
      acc[name] = (acc[name] || 0) + 1;
      return acc;
    }, {});

    fileEntries.forEach(({ partyKey, field, name }) => {
      if (nameCounts[name] > 1) {
        keys[partyKey] = {
          ...keys[partyKey],
          [field]: `Duplicate file name "${name}". Each uploaded file must have a unique name.`,
        };
      }
    });

    if (Object.keys(keys).length !== 0) {
      setErrors(keys);
      showNotification({
        color: "red",
        title: "Agreement Form",
        message: "Please check all fields are filled properly.",
      });
    } else {
      setErrors({});
    }

    return Object.keys(keys).length === 0;
  };

  const savePrev = async () => {
    setUiConfigs({
      ...uiConfigs,
      currentStep: 0,
    });
  };

  const saveNext = async () => {
    if (!validate()) return;

    const valuesToSave = {
      ...legacyAgreementFormValue,
      formCompleted: true,
    };
    const docs = [];

    PARTY_KEYS.forEach((party) => {
      const partyData = valuesToSave[party];
      if (
        !partyData ||
        typeof partyData !== "object" ||
        Object.keys(partyData).length === 0
      ) {
        return;
      }

      const nextParty = { ...partyData };
      KYC_FILE_FIELDS.forEach((field) => {
        const file = nextParty[field];
        if (file instanceof File) {
          docs.push(file);
          nextParty[field] = { name: file.name, field };
        } else if (hasUpload(file)) {
          nextParty[field] = {
            name: file.name,
            field: file.field || field,
            ...(file.fileId ? { fileId: file.fileId } : {}),
            ...(file.hostedLink ? { hostedLink: file.hostedLink } : {}),
          };
        }
      });
      valuesToSave[party] = nextParty;
    });

    setLoader(true);
    const res = docs.length === 0 ? "fulfilled" : await addFiles(docs);
    if (res === "fulfilled") {
      setLegacyAgreementFormValue(valuesToSave);
      const saved = await saveAgreement(flat(valuesToSave));
      setLoader(false);
      if (saved) {
        setUiConfigs({
          ...uiConfigs,
          currentStep: 2,
        });
      }
    } else {
      showNotification({
        color: "red",
        title: "Uploading Documents",
        message: "Unable to upload documents! Please try again.",
      });
      setLoader(false);
    }
  };

  const handlePartySelect = (partyKey, selectedName) => {
    const selectedPartyData = parties.find((p) => p.name === selectedName);

    setLegacyAgreementFormValue((prev) => {
      const currentParty = prev[partyKey] || {};

      if (selectedPartyData) {
        return {
          ...prev,
          [partyKey]: {
            ...currentParty,
            name: selectedName,
            type: selectedPartyData.type || "Entity",
            entityType:
              selectedPartyData.companyType ||
              selectedPartyData.entityType ||
              "Company",
            brandName: selectedPartyData.brandName || "",
            email: selectedPartyData.email || "",
            pocemail: selectedPartyData.email || "",
            signatory: selectedPartyData.signatory || "",
            representative: selectedPartyData.representative || "",
            address:
              selectedPartyData.registeredOfficeAddress ||
              selectedPartyData.residenceOf ||
              "",
            corporateAddress: selectedPartyData.corporateOfficeAddress || "",
          },
        };
      }

      return {
        ...prev,
        [partyKey]: {
          type: currentParty.type || "Entity",
          partyType: currentParty.partyType || "",
          partyCode: currentParty.partyCode || "",
          name: selectedName,
          entityType: "",
          brandName: "",
          email: "",
          pocemail: "",
          signatory: "",
          representative: "",
          address: "",
          corporateAddress: "",
          pocphone: "",
          gstNo: "",
          panNo: "",
          aadhaarNo: "",
          uploadGST: null,
          uploadPAN: null,
          uploadAadhaar: null,
          uploadOtherKYCDocument: null,
        },
      };
    });
  };

  return (
    <div className="w-full flex flex-col">
      <div className="flex flex-col justify-between">
        {PARTY_KEYS.slice(0, addParty).map((partyName) => (
          <LegacyAgreementParty
            key={partyName}
            errors={errors[partyName]}
            clearPartyErrors={clearPartyErrors}
            legacyAgreementFormValue={legacyAgreementFormValue}
            dropDownNames={dropDownNames}
            setLegacyAgreementFormValue={setLegacyAgreementFormValue}
            onPartySelect={
              partyName === "firstParty" ? undefined : handlePartySelect
            }
            partyName={partyName}
            setDropDownNames={setDropDownNames}
            isReadOnly={partyName === "firstParty"}
            dataRole={dataRole}
            setDataRole={setDataRole}
          />
        ))}
      </div>
      <div
        className={`flex ${addParty > 2 ? "justify-between" : "justify-end"} items-center mt-4`}
      >
        {addParty > 2 && (
          <Button
            onClick={() => setAddParty((pre) => pre - 1)}
            className="bg-red-500 hover:bg-red-600"
          >
            Delete Party
          </Button>
        )}
        {addParty < 20 && (
          <Button
            style={{
              backgroundColor: "#46BDE1",
            }}
            onClick={() => setAddParty((pre) => pre + 1)}
          >
            Add Party
          </Button>
        )}
      </div>
      <div className="flex items-center justify-between mt-20">
        <Button
          className="max-w-xs"
          onClick={savePrev}
          style={{
            backgroundColor: "#46BDE1",
          }}
          disabled={uiConfigs.loading === loadingStates.LOADING}
        >
          Previous
        </Button>
        <Button
          className="max-w-xs"
          style={{
            backgroundColor: "#46BDE1",
          }}
          onClick={saveNext}
          disabled={uiConfigs.loading === loadingStates.LOADING || loader}
        >
          {uiConfigs.loading === loadingStates.LOADING || loader ? (
            <BeatLoader color={"#fff"} size={10} />
          ) : (
            "Save & Next"
          )}
        </Button>
      </div>
    </div>
  );
};

export default LegacyAgreementForm3;
